/** New Self lines/cones start at the footprint edge. Stored zones never call this helper. */
export interface AoePoint { x: number; y: number; z: number }
export interface AoeSpan { origin: AoePoint; endpoint: AoePoint; center: AoePoint }
export type AoeFootprint = { kind: 'circle'; r: number } | { kind: 'oblong'; halfW: number; halfD: number; facing: number };

/** Distance along the aim ray to its exit from the fixed, oriented body bounds. */
function edgeDistance(footprint: AoeFootprint, ux: number, uy: number): number {
    if (footprint.kind === 'circle') return footprint.r;
    const c = Math.cos(footprint.facing), s = Math.sin(footprint.facing);
    const x = Math.abs(ux * c + uy * s), y = Math.abs(-ux * s + uy * c);
    return Math.min(x > 1e-9 ? footprint.halfW / x : Infinity, y > 1e-9 ? footprint.halfD / y : Infinity);
}

export function isSelfDirectionalAoe(selfRange: boolean, shape: string): boolean {
    return selfRange && (shape === 'line' || shape === 'cone');
}

/** Re-anchor the span without shortening its length or changing its pitch. The footprint is planar;
 * elevation remains at the caller's body/launch height. Reapplying this to an edge span is idempotent. */
export function selfAoeEdgeSpan(selfRange: boolean, shape: string, caster: AoePoint, footprint: AoeFootprint | number,
    origin: AoePoint, endpoint: AoePoint): AoeSpan {
    const delta = { x: endpoint.x - origin.x, y: endpoint.y - origin.y, z: endpoint.z - origin.z };
    const run = Math.hypot(delta.x, delta.y);
    const distance = run > 1e-9 ? edgeDistance(typeof footprint === 'number' ? { kind: 'circle', r: footprint } : footprint,
        delta.x / run, delta.y / run) : 0;
    const edge = isSelfDirectionalAoe(selfRange, shape) && distance > 0 && run > 1e-9
        ? { x: caster.x + delta.x / run * distance, y: caster.y + delta.y / run * distance, z: origin.z }
        : origin;
    const end = edge === origin ? endpoint : { x: edge.x + delta.x, y: edge.y + delta.y, z: edge.z + delta.z };
    return { origin: edge, endpoint: end,
        center: { x: (edge.x + end.x) / 2, y: (edge.y + end.y) / 2, z: (edge.z + end.z) / 2 } };
}
